import type { ComponentType, SVGProps } from 'react';
import type { Theme } from '@/styles/theme/const';
import { ICON_NAMES, type IconName } from './types';

export type SvgIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export type IconPaint = 'stroke' | 'fill' | 'both' | 'fixed';

export interface IconRegistryItem {
  component: SvgIconComponent;
  paint: IconPaint;
}

type ThemeIconPaintConfig = {
  defaultPaint: Exclude<IconPaint, 'fixed'>;
  overrides: Partial<Record<IconName, IconPaint>>;
};

const iconModules = import.meta.glob('@icons-current/*.svg', {
  eager: true,
  import: 'default',
  query: '?react',
}) as Record<string, SvgIconComponent>;

const componentsByName = Object.fromEntries(
  ICON_NAMES.map((name) => {
    const moduleEntry = Object.entries(iconModules).find(([path]) => path.endsWith(`/${name}.svg`));

    if (!moduleEntry) {
      throw new Error(`[IconCollection] Missing icon in current pack: ${name}.svg`);
    }

    return [name, moduleEntry[1]];
  })
) as Record<IconName, SvgIconComponent>;

const commonPaintOverrides: Partial<Record<IconName, IconPaint>> = {
  Check_All_Big: 'fixed',
  Coin: 'fixed',
  EUR: 'fixed',
  Loading: 'fixed',
  RUB: 'fixed',
  USD: 'fixed',
  User_Big: 'fixed',
  Users_Group: 'fixed',
  WarningWaveFill: 'fixed',
  notifications_Check: 'fixed',
  Stop_Sign: 'fixed',
  Tooltip_Info: 'fixed',
};

// Экспорт нужен гейту packs.test.ts: он сверяет режим окраски с содержимым SVG
// во всех паках, а активный пак в vitest всегда один (serious).
export const paintByTheme: Record<Theme, ThemeIconPaintConfig> = {
  serious: {
    defaultPaint: 'stroke',
    overrides: {
      ...commonPaintOverrides,
      Money: 'fill',
      Checkbox_On: 'fill',
      Warning: 'fixed',
    },
  },
  wap: {
    defaultPaint: 'fill',
    overrides: {
      Add_Plus: 'stroke',
      Arrow_down: 'both',
      Arrow_Left: 'both',
      Arrows_Reload: 'fill',
      Bell: 'fill',
      Burger: 'stroke',
      Caret_Down_Little: 'both',
      Check_Big: 'stroke',
      Check: 'stroke',
      Check_Double: 'stroke',
      Checkbox_Off: 'stroke',
      Checkbox_On: 'fill',
      Chevron: 'stroke',
      Close: 'stroke',
      Search: 'stroke',
      External_Link: 'stroke',
      Files: 'stroke',
      // One_Circle раньше была обводкой; с иконками режимов показа от дизайнера
      // (30.07) все четыре — заливка, то есть дефолтный для пака режим.
      Pen: 'stroke',
      Sort_Ascending: 'stroke',
      Sort_Descending: 'stroke',
      Trash: 'stroke',
      Unfold_More: 'both',
      Warning: 'fill',

      // Пак нарисован заливкой, но эти пять пришли от дизайнера обводкой:
      // fill="none" на корне, цвет прибит в stroke. Под дефолтным для пака
      // 'fill' контур заливается целиком — Clock превращался в сплошной круг
      // (статус тикета «Есть ответ»), Mail — в прямоугольник, а прибитый цвет
      // обводки не отзывался на color, из-за чего не работал hover у стрелок
      // пагинации и кнопки «Наверх».
      Arrow_Left_SM: 'stroke',
      Arrow_Up_LG: 'stroke',
      Clock: 'stroke',
      Mail: 'stroke',
      More_Horizontal: 'stroke',
      // Star и Qr_Code — копии serious-иконок (см. ICON_NAMES), обе обводкой.
      Star: 'stroke',
      Qr_Code: 'stroke',
      // Network нарисован обводкой во всех паках (см. ICON_NAMES).
      Network: 'stroke',

      ...commonPaintOverrides,
    },
  },
  // Пак telecom — копия serious (обводка по умолчанию), в которой иконки меню
  // заменены контурами Lucide из макета. Money там кошелёк обводкой, а не
  // силуэт serious, поэтому заливочного оверрайда у него нет.
  telecom: {
    defaultPaint: 'stroke',
    overrides: {
      ...commonPaintOverrides,
      Checkbox_On: 'fill',
      // У telecom «i» и «!» — контурные из Lucide (макет): цвет берут от
      // текста, а не прибиты синим, как у serious.
      Warning: 'stroke',
      Tooltip_Info: 'stroke',
    },
  },
};

// VITE_THEME проверяет vite.config; фолбэк нужен только тестам (в mode 'test'
// env бренда не грузится, а алиас пака указывает на serious).
const currentTheme: Theme = import.meta.env.VITE_THEME ?? 'serious';
const currentPaintConfig = paintByTheme[currentTheme];

export const iconRegistry = Object.fromEntries(
  ICON_NAMES.map((name) => [
    name,
    {
      component: componentsByName[name],
      paint: currentPaintConfig.overrides[name] ?? currentPaintConfig.defaultPaint,
    },
  ])
) as Record<IconName, IconRegistryItem>;
